import React, { Suspense } from "react";
import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import AppLayout from "./components/AppLayout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Competition from "./pages/Competition";
import Admin from "./pages/Admin";
import PublicLeaderboard from "./pages/PublicLeaderboard";
import DeveloperPortal from "./pages/DeveloperPortal";
import PromoterPage from "./pages/PromoterPage";
import ReferralLanding from "./pages/ReferralLanding";
import LandingPage from "./pages/LandingPage";
import InvestmentProfiles from "./pages/InvestmentProfiles";
import Onboarding from "./pages/Onboarding";
import IntangiTrading from "./pages/IntangiTrading";
import WalletPage from "./pages/Wallet";
import ReportPage from "./pages/Report";
import RiskDashboard from "./pages/RiskDashboard";
import TradeJournal from "./pages/TradeJournal";
import Rewards from "./pages/Rewards";
import SDKPage from "./pages/SDKPage";
import TCAAnalytics from "./pages/TCAAnalytics";
import InfraDashboard from "./pages/InfraDashboard";
import QuantResearchLab from "./pages/QuantResearchLab";
import ComputeCommodities from "./pages/ComputeCommodities";
import AiChatWidget from "./components/AiChatWidget";

// Chat widget - only shows when authenticated
function AuthenticatedChatWidget() {
  const { user, loading } = useAuth();
  if (loading || !user || user === false) return null;
  return <AiChatWidget />;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 font-mono text-sm">VERIFYING ACCESS...</p>
        </div>
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route - redirect to dashboard if already logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user && user !== false) {
    if (user.onboarding_complete === false) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/leaderboard" element={<PublicLeaderboard />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <AppLayout><Dashboard /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/competition" 
        element={
          <ProtectedRoute>
            <AppLayout><Competition /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AppLayout><Admin /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/developers" 
        element={
          <ProtectedRoute>
            <AppLayout><DeveloperPortal /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/promoter" 
        element={
          <ProtectedRoute>
            <AppLayout><PromoterPage /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route path="/ref/:code" element={<ReferralLanding />} />
      <Route path="/profiles" element={<ProtectedRoute><AppLayout><InvestmentProfiles /></AppLayout></ProtectedRoute>} />
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/intangi" 
        element={
          <ProtectedRoute>
            <AppLayout><IntangiTrading /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/wallet" 
        element={
          <ProtectedRoute>
            <AppLayout><WalletPage /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/report" 
        element={
          <ProtectedRoute>
            <AppLayout><ReportPage /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/risk" 
        element={
          <ProtectedRoute>
            <AppLayout><RiskDashboard /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/journal" 
        element={
          <ProtectedRoute>
            <AppLayout><TradeJournal /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rewards" 
        element={
          <ProtectedRoute>
            <AppLayout><Rewards /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sdk" 
        element={
          <ProtectedRoute>
            <AppLayout><SDKPage /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tca" 
        element={
          <ProtectedRoute>
            <AppLayout><TCAAnalytics /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/infrastructure" 
        element={
          <ProtectedRoute>
            <AppLayout><InfraDashboard /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/quant-lab" 
        element={
          <ProtectedRoute>
            <AppLayout><QuantResearchLab /></AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/compute" 
        element={
          <ProtectedRoute>
            <AppLayout><ComputeCommodities /></AppLayout>
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <Suspense fallback={
        <div className="min-h-screen bg-[#030508] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      }>
        <BrowserRouter>
          <AuthProvider>
            <CurrencyProvider>
              <AppRoutes />
              <AuthenticatedChatWidget />
            </CurrencyProvider>
          </AuthProvider>
        </BrowserRouter>
      </Suspense>
    </div>
  );
}

export default App;
